/* eslint-disable */
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ECharts from "echarts-for-react";
import { Box, Tab, Tabs } from "@mui/material";
import dayjs from "utils/dayjs";
import MainCard from "components/MainCard";
import usePolling from "./usePolling";
import useConfig from "hooks/useConfig";
import { getEquipmentMeasurementLogsStatsComparisonQueryString } from "components/axios/equipmentAxios";
import { getMeasurementCodesByQuery } from "components/axios/specAxios";
import { isSameData } from "utils/graphUtils";

const DEVICE_TIMEZONE = process.env.REACT_APP_TIMEZONE || "Asia/Seoul";
const DELAY_TIME = Number(process.env.REACT_APP_DELAY_TIME) || 60000;

function EquipmentPartMeasurementGraphs({ equipmentPartId, componentLocationId }) {
    const { t } = useTranslation();
    const { startPolling, stopPolling } = usePolling();
    const { mode } = useConfig();

    const textColor = mode === "light" ? "#262626" : "#ffffff";
    const bgColor = mode === "light" ? "#f9f9fa" : "#2f2f2f";

    const [measurementCodes, setMeasurementCodes] = useState([]); // API로 받아올 코드 목록
    const [loading, setLoading] = useState(true); // 로딩 상태
    const [value, setValue] = useState(null);
    const [autoLocked, setAutoLocked] = useState(false);
    const [uiReady, setUiReady] = useState({});
    const [isVisible, setIsVisible] = useState(typeof document !== "undefined" ? !document.hidden : true);

    const pollingRef = useRef(null);
    const chartRefs = useRef({}); // 모든 차트의 ref를 객체로 관리
    const prevDataRefs = useRef({}); // 이전 데이터 비교용 ref

    const graphParam = `?componentLocation.id=${componentLocationId}&equipmentPart.id=${equipmentPartId}`;
    const paramType = "DAY";

    /* ---------------- 1. API 호출: measurementCodes 가져오기 ---------------- */
    useEffect(() => {
        const fetchCodes = async () => {
            try {
                setLoading(true);
                const param = { 
                    page : 0,
                    size : 100,
                    sort : "code,ASC" 
                };
                
                // 실제 프로젝트의 API 경로 및 구조에 맞춰 수정하세요
                const response = await getMeasurementCodesByQuery(param);
                // 예제 데이터 구조: response.data.measurementCodes 또는 response.data
                const codes = response._embedded?.measurementCodes || [];
                setMeasurementCodes(codes);
            } catch (error) {
                console.error("Failed to fetch measurement codes:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCodes();
    }, []); // 컴포넌트 마운트 시 최초 1회 실행

    /* ---------------- 1. 그래프 타입 정의 (고정 묶음 + 동적 추가) ---------------- */
    const GRAPH_TYPES = useMemo(() => {
        if (!measurementCodes.length) return {};

        // 1. 화면에 절대 표시하고 싶지 않은 코드들을 여기에 정의합니다.
        const EXCLUDE_CODES = process.env.REACT_APP_TEMPERATURE_VIEW_YN === 'Y' ? ["AL", "ENA", "ST", "W_Time", "M_Time", "ACCELERATING", "DECELERATING"
            , "CONSTANT", "UP", "DOWN", "LEFT", "RIGHT", "FORWARD", "BACKWARD", "STOP", "OP", "MODE", "CMT"] 
            : ["AL", "ENA", "ST", "W_Time", "M_Time", "ACCELERATING", "DECELERATING"
            , "CONSTANT", "UP", "DOWN", "LEFT", "RIGHT", "FORWARD", "BACKWARD", "STOP", "OP", "T", "T_ST", "T_SPT", "MODE", "CMT"];

        // . 기존에 정의된 묶음 설정
        const FIXED_GROUPS = {
            ...(process.env.REACT_APP_TEMPERATURE_VIEW_YN === 'Y' && {
                temperature: {
                    key: "temperature",
                    tabLabel: t("message.common.subtitle.temperature.graph"),
                    codes: ["T", "T_ST", "T_SPT"],
                    buildData: (list) => ({
                        x: [...new Set(list.map(v => v.ymdh))],
                        series: [
                            { name: list.find(v => v.code === "T")?.codeName || t("message.common.subtitle.temperature"), code: "T", color: "blue" },
                            { name: list.find(v => v.code === "T_ST")?.codeName || t("message.common.subtitle.temperature.panel"), code: "T_ST", color: "gray" },
                            { name: list.find(v => v.code === "T_SPT")?.codeName || t("message.common.subtitle.temperature.setting"), code: "T_SPT", color: "red" }
                        ].filter(s => list.some(v => v.code === s.code)).map(s => ({
                            name: s.name, type: "line", data: list.filter(v => v.code === s.code && !isNaN(Number(v.avgValue))).map(v => +Number(Number(v.avgValue).toFixed(2))),
                            lineStyle: { width: 5, color: s.color }, showSymbol: false, smooth: true
                        })),
                        codeUnit: list[0]?.codeUnit || '℃'
                    })
                },
            }),
            co2: {
                key: "co2",
                tabLabel: t("message.common.subtitle.co2.graph"),
                codes: ["CO2"],
                buildData: (list) => ({
                    x: [...new Set(list.map(v => v.ymdh))],
                    series: [{ name: list[0]?.codeName || t("message.common.subtitle.co2"), type: "line", data: list.map(v => +v.avgValue.toFixed(2)), lineStyle: { width: 5 }, showSymbol: false, smooth: true }],
                    codeUnit: list[0]?.codeUnit || 'ppm'
                })
            },
            rh: {
                key: "rh",
                tabLabel: t("message.common.subtitle.humidity.graph"),
                codes: ["RH","RH_ST","RH_SPT"],
                buildData: (list) => ({
                    x: [...new Set(list.map(v => v.ymdh))],
                    series: [
                        { name: list.find(v => v.code === "RH")?.codeName || t("message.common.subtitle.humidity"), code: "RH" },
                        { name: list.find(v => v.code === "RH_ST")?.codeName || t("message.common.subtitle.humidity.panel"), code: "RH_ST" },
                        { name: list.find(v => v.code === "RH_SPT")?.codeName || t("message.common.subtitle.humidity.setting"), code: "RH_SPT"}
                    ].filter(s => list.some(v => v.code === s.code)).map(s => ({
                        name: s.name, type: "line", data: list.filter(v => v.code === s.code && !isNaN(Number(v.avgValue))).map(v => +Number(Number(v.avgValue).toFixed(2))),
                        lineStyle: { width: 5 }, showSymbol: false, smooth: true
                    })),
                    codeUnit : list[0]?.codeUnit || '%'
                })
            },
            pm: {
                key: "pm",
                tabLabel: t("message.common.subtitle.air.quality.graph"),
                codes: ["PM10", "PM25", "TVOC", "HCHO"],
                buildData: (list) => ({
                    x: [...new Set(list.map(v => v.ymdh))],
                    series: [
                        { name: list.find(v => v.code === "PM10")?.codeName || t("message.common.subtitle.pm10"), code: "PM10" },
                        { name: list.find(v => v.code === "PM25")?.codeName || t("message.common.subtitle.pm25"), code: "PM25" },
                        { name: list.find(v => v.code === "TVOC")?.codeName || t("message.common.subtitle.organic.compounds"), code: "TVOC" },
                        { name: list.find(v => v.code === "HCHO")?.codeName || t("message.common.subtitle.formaldehyde"), code: "HCHO" }
                    ].filter(s => list.some(v => v.code === s.code)).map(s => ({
                        name: s.name, type: "line", data: list.filter(v => v.code === s.code && !isNaN(Number(v.avgValue))).map(v => +Number(Number(v.avgValue).toFixed(2))),
                        lineStyle: { width: 5 }, showSymbol: false, smooth: true
                    })),
                    codeUnit : list[0]?.codeUnit || 'µg/m³'
                })
            },
            pos: {
                key: "pos",
                tabLabel: t("message.common.subtitle.opening.rate.graph"),
                codes: ["POS", "POS_SPT"],
                buildData: (list) => ({
                    x: [...new Set(list.map(v => v.ymdh))],
                    series: [
                        { name: list.find(v => v.code === "POS")?.codeName || t("message.common.subtitle.opening.rate"), code: "POS" },
                        { name: list.find(v => v.code === "POS_SPT")?.codeName || t("message.common.subtitle.opening.degree.setting"), code: "POS_SPT" }
                    ].filter(s => list.some(v => v.code === s.code)).map(s => ({
                        name: s.name, type: "line", data: list.filter(v => v.code === s.code && !isNaN(Number(v.avgValue))).map(v => +Number(Number(v.avgValue).toFixed(2))),
                        lineStyle: { width: 5 }, showSymbol: false, smooth: true
                    })),
                    codeUnit : list[0]?.codeUnit || '%'
                })
            },
            static: {
                key: "static",
                tabLabel: t("message.common.subtitle.static.graph"),
                codes: ["STATIC", "STATIC_SPT","DP"],
                buildData: (list) => ({
                    x: [...new Set(list.map(v => v.ymdh))],
                    series: [
                        { name: list.find(v => v.code === "STATIC")?.codeName || t("message.common.subtitle.static.pressure"), code: "STATIC" },
                        { name: list.find(v => v.code === "STATIC_SPT")?.codeName || t("message.common.subtitle.static.pressure.setting"), code: "STATIC_SPT" },
                        { name: list.find(v => v.code === "DP")?.codeName || t("message.common.subtitle.foreclosure"), code: "DP" }
                    ].filter(s => list.some(v => v.code === s.code)).map(s => ({
                        name: s.name, type: "line", data: list.filter(v => v.code === s.code && !isNaN(Number(v.avgValue))).map(v => +Number(Number(v.avgValue).toFixed(2))),
                        lineStyle: { width: 5 }, showSymbol: false, smooth: true
                    })),
                    codeUnit : list[0]?.codeUnit || '%'
                })
            },
            speed: {
                key: "speed",
                tabLabel: t("message.common.subtitle.speed.graph"),
                codes: ["SP"],
                buildData: (list) => ({
                    x: [...new Set(list.map(v => v.ymdh))],
                    series: [
                        { name: list.find(v => v.code === "SP")?.codeName || t("message.common.subtitle.speed"), code: "SP" },
                    ].filter(s => list.some(v => v.code === s.code)).map(s => ({
                        name: s.name, type: "line", data: list.filter(v => v.code === s.code && !isNaN(Number(v.avgValue))).map(v => +Number(Number(v.avgValue).toFixed(2))),
                        lineStyle: { width: 5 }, showSymbol: false, smooth: true
                    })),
                    codeUnit : list[0]?.codeUnit || 'rpm'
                })
            },
            speed2: {
                key: "speed2",
                tabLabel: t("message.common.subtitle.speed.graph"),
                codes: ["SPpct"],
                buildData: (list) => ({
                    x: [...new Set(list.map(v => v.ymdh))],
                    series: [
                        { name: list.find(v => v.code === "SPpct")?.codeName || t("message.common.subtitle.speed"), code: "SPpct" },
                    ].filter(s => list.some(v => v.code === s.code)).map(s => ({
                        name: s.name, type: "line", data: list.filter(v => v.code === s.code && !isNaN(Number(v.avgValue))).map(v => +Number(Number(v.avgValue).toFixed(2))),
                        lineStyle: { width: 5 }, showSymbol: false, smooth: true
                    })),
                    codeUnit : list[0]?.codeUnit || '%'
                })
            },
            electric: {
                key: "electric",
                tabLabel: t("message.common.subtitle.electric.graph"),
                codes: ["CUR","PWR","VLT","FRQ"],
                buildData: (list) => ({
                    x: [...new Set(list.map(v => v.ymdh))],
                    series: [
                        { name: list.find(v => v.code === "CUR")?.codeName , code: "CUR" },
                        { name: list.find(v => v.code === "PWR")?.codeName , code: "PWR" },
                        { name: list.find(v => v.code === "VLT")?.codeName , code: "VLT" },
                        { name: list.find(v => v.code === "FRQ")?.codeName , code: "FRQ" },
                    ].filter(s => list.some(v => v.code === s.code)).map(s => ({
                        name: s.name, type: "line", data: list.filter(v => v.code === s.code && !isNaN(Number(v.avgValue))).map(v => +Number(Number(v.avgValue).toFixed(2))),
                        lineStyle: { width: 5 }, showSymbol: false, smooth: true
                    })),
                    codeUnit : list[0]?.codeUnit || ''
                })
            },
        };
// console.log(FIXED_GROUPS)
        // B. 묶음에 포함된 모든 코드 추출
        const groupedCodes = new Set(Object.values(FIXED_GROUPS).flatMap(g => g.codes));

        // C. API로 받은 코드 중 묶음에 없는 것들을 단일 그래프로 생성
        const dynamicGroups = {};
        measurementCodes.forEach(item => {
            // console.log(item)
            // 조건 1: 이미 FIXED_GROUPS에 포함되어 있는가?
            const isGrouped = groupedCodes.has(item.code);
            
            // 조건 2: 제외 목록(EXCLUDE_CODES)에 포함되어 있는가?
            const isExcluded = EXCLUDE_CODES.includes(item.code);
            
            // 두 조건에 모두 해당하지 않는 경우에만 단일 그래프 탭 생성
            if (!isGrouped && !isExcluded) {
                dynamicGroups[item.code] = {
                    key: item.code,
                    tabLabel: item.name,
                    codes: [item.code],
                    buildData: (list) => ({
                        x: [...new Set(list.map(v => v.ymdh))],
                        series: [{ name: item.name, type: "line", data: list.map(v => +v.avgValue.toFixed(2)), lineStyle: { width: 5 }, showSymbol: false, smooth: true }],
                        codeUnit: item.unit || ''
                    })
                };
            }
        });
// console.log(dynamicGroups)
        return { ...FIXED_GROUPS, ...dynamicGroups };
    }, [measurementCodes, t]);
// console.log(GRAPH_TYPES)

    const TAB_ORDER = useMemo(() => Object.keys(GRAPH_TYPES), [GRAPH_TYPES]);

    /* ---------------- 2. 유틸리티 함수 ---------------- */
    const getTimeKey = (d) => {
        const dateStr = `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
        const timeStr = paramType === "DAY" ? `${String(d.hour).padStart(2, '0')}:00:00` : "00:00:00";
        return dayjs.tz(`${dateStr} ${timeStr}`, DEVICE_TIMEZONE).valueOf();
    };

    const buildList = (data) => {
        return [...data].sort((a, b) => getTimeKey(a) - getTimeKey(b)).map(d => {
            const dt = dayjs(getTimeKey(d)).tz(DEVICE_TIMEZONE);
            const formatMap = { DAY: "YYYY.MM.DD HH:00", MONTH: "YYYY.MM.DD" };
            return {
                ymdh: dt.format(formatMap[paramType] || "YYYY.MM"),
                code: d.code,
                codeName: d.codeName || '',
                codeUnit: d.codeUnit || '',
                avgValue: d.avgValue,
            };
        });
    };

    const drawChart = (key, data) => {
        const chart = chartRefs.current[key]?.getEchartsInstance();
        if (!chart) return;

        chart.setOption({
            textStyle: { color: textColor },
            tooltip: { trigger: "axis" },
            legend: { left: "center", top: 0, backgroundColor: bgColor, textStyle: { color: textColor } },
            grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
            xAxis: { type: "category", boundaryGap: false, data: data.x },
            yAxis: { type: "value", name: data.codeUnit },
            series: data.series
        }, false);
    };

    /* ---------------- 3. Effects ---------------- */
    
    // 가시성 감지
    useEffect(() => {
        const onVisible = () => setIsVisible(!document.hidden);
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, []);

    useEffect(() => {
        // 탭이 바뀌었을 때(value), 이미 해당 탭의 데이터가 존재한다면 다시 그림
        if (value && uiReady[value] && prevDataRefs.current[value]) {
            // 차트 인스턴스가 DOM에 렌더링될 시간을 아주 잠깐 주기 위해 setTimeout 사용
            const timer = setTimeout(() => {
                drawChart(value, prevDataRefs.current[value]);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [value, uiReady]); // value가 바뀔 때마다 실행

    // 데이터 페칭 및 폴링
    useEffect(() => {
        if (!componentLocationId || !isVisible || !value) return;
        const graph = GRAPH_TYPES[value];
        if (!graph) return;

        const fetchData = async () => {
            const query = graph.codes.map(c => `&code=${c}`).join("");
            const data = await getEquipmentMeasurementLogsStatsComparisonQueryString(paramType, graphParam + query);

            if (!data || !data.length) {
                prevDataRefs.current[value] = null;
                chartRefs.current[value]?.getEchartsInstance()?.clear();
                setUiReady(v => ({ ...v, [value]: false }));
                return;
            }

            const list = buildList(data);
            const next = graph.buildData(list);

            if (!next.x?.length) return;
            if (isSameData(prevDataRefs.current[value], next)) return;

            prevDataRefs.current[value] = next;
            setUiReady(v => ({ ...v, [value]: true }));
            drawChart(value, next);
        };

        fetchData();
        startPolling(pollingRef, fetchData, DELAY_TIME);
        return () => stopPolling(pollingRef);
    }, [value, componentLocationId, isVisible, GRAPH_TYPES]);

    // 초기 UI 가용성 체크
    useEffect(() => {
        if (!componentLocationId || !isVisible || !Object.keys(GRAPH_TYPES).length) return;

        const checkAllTabs = async () => {
            // 1. 모든 그래프 타입에 대해 데이터 체크 프로미스 생성
            const checkPromises = Object.values(GRAPH_TYPES).map(async (graph) => {
                const query = graph.codes.map(c => `&code=${c}`).join("");
                const data = await getEquipmentMeasurementLogsStatsComparisonQueryString(paramType, graphParam + query);
                return {
                    key: graph.key,
                    hasData: data && data.length > 0
                };
            });

            // 2. 모든 응답이 올 때까지 기다림 (순서 보장의 핵심)
            const results = await Promise.all(checkPromises);

            // 3. 결과를 하나의 객체로 합침
            const nextUiReady = {};
            results.forEach(res => {
                nextUiReady[res.key] = res.hasData;
            });

            // 4. 상태를 한 번에 업데이트 (이 시점에 탭들이 순서대로 한꺼번에 생김)
            setUiReady(nextUiReady);

            // 5. 업데이트된 정보를 바탕으로 첫 번째 탭 자동 선택
            const firstAvailable = TAB_ORDER.find(k => nextUiReady[k] === true);
            if (firstAvailable && value === null) {
                setValue(firstAvailable);
            }
        };

        checkAllTabs();
    }, [componentLocationId, isVisible, GRAPH_TYPES]);

    // 자동 탭 선택 로직
    useEffect(() => {
        // 이미 값이 있거나, 아직 uiReady에 아무것도 없다면 중단
        if (value !== null || Object.keys(uiReady).length === 0) return;

        // 중요: TAB_ORDER(순서 보장)를 기준으로 루프를 돌며 
        // 데이터가 있는(true) 가장 첫 번째 탭을 찾습니다.
        const firstAvailable = TAB_ORDER.find(key => uiReady[key] === true);

        if (firstAvailable) {
            setValue(firstAvailable);
        }
    }, [uiReady, value, TAB_ORDER]);

    // 초기화
    useEffect(() => {
        setValue(null);
        setAutoLocked(false);
        setUiReady({});
        prevDataRefs.current = {};
    }, [componentLocationId]);

    if (loading) return null; // 또는 로딩 스피너 표시
    if (!Object.values(uiReady).some(v => v === true)) return null;

    return (
        <React.Fragment>
            <div className="row">
                <div className="mixed-chart">
                    <Box>
                        <Tabs value={value} onChange={(e, v) => { setValue(v); setAutoLocked(true); }} variant="scrollable" scrollButtons="auto">
                            {Object.values(GRAPH_TYPES).map(g => 
                                uiReady[g.key] && (
                                    <Tab 
                                        key={g.key} 
                                        value={g.key} 
                                        label={g.tabLabel} 
                                        className="graphTab" 
                                        style={{ border: value === g.key ? "3px solid #0061d0" : "1px solid #0061d0" }} 
                                    />
                                )
                            )}
                        </Tabs>
                        <Box mt={2}>
                            {Object.values(GRAPH_TYPES).map(g => 
                                value === g.key && uiReady[g.key] && (
                                    <MainCard key={g.key} content={false}>
                                        <ECharts 
                                            ref={el => chartRefs.current[g.key] = el}
                                            option={{
                                                textStyle: { color: textColor },
                                                xAxis: { type: "category", boundaryGap: false },
                                                yAxis: { type: "value" },
                                                grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true }
                                            }}
                                            opts={{ renderer: "svg", height: 300 }} 
                                        />
                                    </MainCard>
                                )
                            )}
                        </Box>
                    </Box>
                </div>
            </div>
            <div className="divide" />
        </React.Fragment>
    );
}

export default EquipmentPartMeasurementGraphs;